import React, { useState } from 'react';
import { showMessage } from '../utils/messageUtils';
import { onboardAPI } from '../services/api';
import { IconArrowLeft, IconArrowRight, IconHourglass, IconCheckCircle } from './Icons';
import { IconBriefcase, IconX, IconBuilding, IconServer, IconDiagram3, IconLightbulb, IconEye, IconCheck } from './onboarding/OnboardingIcons';
import OrganizationInfoForm from './onboarding/OrganizationInfoForm';
import ServiceEndpointsForm from './onboarding/ServiceEndpointsForm';
import AssetPatternsForm from './onboarding/AssetPatternsForm';
import SearchKeywordsForm from './onboarding/SearchKeywordsForm';
import OnboardPreview from './onboarding/OnboardPreview';

const SUPPORTED_ENDPOINT_KEYS = new Set([
  'AAS Discovery',
  'AAS Registry',
  'AAS Repository',
  'Submodel Registry',
  'Submodel Repository',
  'Concept Description Repository',
  'DPP Service'
]);

const OnboardModal = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Domain
    domain: '',
    
    // Company Data. Only the fields discovery actually uses - see the scope
    // note in OrganizationInfoForm. The legal, tax and bank-account fields that
    // used to live here were written to descriptors.json and never read back.
    company_data: {
      company_name: '',
      company_description: '',
      homepage_url: ''
    },
    
    // Capabilities
    capabilities: {
      language: 'EN',
      capability_description: '',
      keywords: [],
      capability_types: [],
      submodels_supported: [],
      asset_kind: 'Instance'
    },
    
    // Infrastructure Endpoints
    infrastructure_endpoints: {
      'AAS Discovery': '',
      'AAS Registry': '',
      'AAS Repository': '',
      'Submodel Registry': '',
      'Submodel Repository': '',
      'Concept Description Repository': '',
      'DPP Service': ''
    },
    
    // Asset ID Pattern
    asset_id_pattern: {
      asset_id_starts_with: '',
      asset_id_ends_with: '',
      asset_id_contains: '',
      global_asset_id: '',
      serial_number_pattern: {
        serial_number_starts_with: '',
        serial_number_ends_with: '',
        serial_number_contains: '',
        serial_number_format: ''
      }
    }
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const steps = [
    { id: 1, title: 'Company', icon: IconBuilding, component: OrganizationInfoForm },
    { id: 2, title: 'Endpoints', icon: IconServer, component: ServiceEndpointsForm },
    { id: 3, title: 'Patterns', icon: IconDiagram3, component: AssetPatternsForm },
    { id: 4, title: 'Capabilities', icon: IconLightbulb, component: SearchKeywordsForm },
    { id: 5, title: 'Review', icon: IconEye, component: OnboardPreview }
  ];

  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 1: // Company Info
        if (!formData.company_data.company_name.trim()) newErrors.company_name = 'Company name is required';
        if (!formData.domain.trim()) newErrors.domain = 'Domain is required';
        if (formData.company_data.homepage_url && !/^https?:\/\/.+/.test(formData.company_data.homepage_url)) {
          newErrors.homepage_url = 'Invalid website URL';
        }
        break;
        
      case 2: // Infrastructure Endpoints
        // Check if at least one endpoint maps to canonical company lookup interfaces
        const hasEndpoint = Object.entries(formData.infrastructure_endpoints).some(
          ([key, ep]) => SUPPORTED_ENDPOINT_KEYS.has(key) && ep && ep.trim() !== ''
        );
        if (!hasEndpoint) {
          newErrors.infrastructure_endpoints = 'At least one AAS endpoint is required (Discovery, Registry, Repository or Submodel service)';
        }
        break;
      
      default:
        // No validation needed for other steps
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const response = await onboardAPI.submitOrganization(formData);
      
      if (response.status === 201) {
        showMessage('Data provider successfully onboarded!', 'success');
        // Don't close immediately, let user see success message
        setTimeout(() => {
          onClose();
          window.location.reload();
        }, 2000);
      } else {
        showMessage(response.data?.message || 'Failed to onboard data provider', 'error');
      }
    } catch (error) {
      if (error.response?.data?.error) {
        showMessage(`Server Error: ${error.response.data.error}`, 'error');
      } else if (error.response?.data?.message) {
        showMessage(`Server Error: ${error.response.data.message}`, 'error');
      } else if (error.response?.status === 401) {
        showMessage('Authentication failed. Please try again.', 'error');
      } else if (error.response?.status === 500) {
        showMessage('Server error. Please check the server logs.', 'error');
      } else if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        showMessage('Network error. Please check if the server is running on port 50008.', 'error');
      } else if (error.message.includes('timeout')) {
        showMessage('Request timeout. Please try again.', 'error');
      } else {
        showMessage(`Failed to onboard data provider: ${error.message}`, 'error');
      }
      
      // Don't close the modal on error - let user see the error and try again
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const CurrentComponent = steps[currentStep - 1].component;
  const currentStepMeta = steps[currentStep - 1];
  const progressLabel = `Step ${currentStep} of ${steps.length}: ${currentStepMeta.title}`;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="onboard-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboard-modal-title"
        aria-describedby="onboard-modal-progress"
      >
        {/* Modal Header */}
        <div className="modal-header">
          <div className="modal-header__text">
            <h2 id="onboard-modal-title">
              <IconBriefcase aria-hidden />
              Register as a data provider
            </h2>
            <p className="modal-header__sub">
              Publish your Asset Administration Shells so others can discover them.
            </p>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            <IconX aria-hidden />
          </button>
        </div>

        {/* Progress Steps. Three states, not two: a finished step shows a tick,
            the current one shows its icon, and an upcoming one shows its number.
            The marker alone tells you where you are, so the label can stay quiet. */}
        <div className="modal-progress" id="onboard-modal-progress" aria-label={progressLabel}>
          {steps.map((step) => {
            const isDone = currentStep > step.id;
            const isCurrent = currentStep === step.id;
            return (
              <button
                key={step.id}
                type="button"
                className={`progress-step${isDone ? ' done' : ''}${isCurrent ? ' current' : ''}`}
                aria-current={isCurrent ? 'step' : undefined}
                // Going back is safe - the form state is kept - so completed
                // steps are reachable without losing what was typed.
                onClick={() => { if (isDone) setCurrentStep(step.id); }}
                disabled={!isDone}
              >
                <span className="step-marker" aria-hidden>
                  {isDone
                    ? <IconCheck className="step-icon" />
                    : isCurrent
                      ? <step.icon className="step-icon" />
                      : step.id}
                </span>
                <span className="step-title">{step.title}</span>
              </button>
            );
          })}
        </div>

        {/* Modal Content */}
        <div className="modal-content">
          <CurrentComponent
            data={formData}
            errors={errors}
            onUpdate={updateFormData}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <div className="footer-buttons">
            {currentStep > 1 && (
              <button
                className="btn btn-secondary btn-previous"
                onClick={handlePrevious}
                disabled={isSubmitting}
              >
                <IconArrowLeft aria-hidden />
                Previous
              </button>
            )}
            
            <div className="footer-spacer"></div>
            
            {currentStep < steps.length ? (
              <button
                className="btn btn-primary btn-next"
                onClick={handleNext}
                disabled={isSubmitting}
              >
                Next
                <IconArrowRight aria-hidden />
              </button>
            ) : (
              <button
                className="btn btn-success btn-submit"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <IconHourglass aria-hidden />
                    Submitting...
                  </>
                ) : (
                  <>
                    <IconCheckCircle aria-hidden />
                    Submit Data Provider
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardModal;
